import React from 'react';
import { useSceneStore } from '../../state/sceneStore';
import { formatInputNumber } from '../../utils/formatting';
import { useUserStore, getAvailableSensorTypes } from '../../state/userStore';

interface SensorDefaultsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SensorDefaultsModal: React.FC<SensorDefaultsModalProps> = ({ isOpen, onClose }) => {
  const {
    defaultSensorHeight,
    defaultSensorRotationRate,
    defaultSensorParameters,
    setDefaultSensorHeight,
    setDefaultSensorRotationRate,
    setDefaultSensorParameters,
    selectedSensorType,
    setSelectedSensorType,
  } = useSceneStore();
  const advancedSensorsEnabled = useUserStore((state) => state.hasFeature('advanced_sensors'));
  const lidarTypes = getAvailableSensorTypes(advancedSensorsEnabled);

  if (!isOpen) {
    return null;
  }

  const modalBackdropStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  };

  const modalContentStyle: React.CSSProperties = {
    background: '#2d3748',
    padding: '20px',
    borderRadius: '8px',
    color: 'white',
    width: '300px',
    boxSizing: 'border-box',
  };

  const inputGroupStyle: React.CSSProperties = {
    marginBottom: '15px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '5px',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #4a5568',
    backgroundColor: '#1a202c',
    color: 'white',
    boxSizing: 'border-box',
    height: '38px',
  };

  const buttonContainerStyle: React.CSSProperties = {
    textAlign: 'right',
    marginTop: '20px',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '10px 20px',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: '#4a5568',
    color: 'white',
    cursor: 'pointer',
  };

  return (
    <div style={modalBackdropStyle} onClick={onClose}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <h2>Sensor Defaults</h2>
        <div style={inputGroupStyle}>
          <label htmlFor="sensorType" style={labelStyle}>
            Default Sensor Type
          </label>
          <select
            id="sensorType"
            value={selectedSensorType}
            onChange={(e) => setSelectedSensorType(e.target.value)}
            style={inputStyle}
          >
            {lidarTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
        <div style={inputGroupStyle}>
          <label htmlFor="defaultHeight" style={labelStyle}>
            Default Sensor Height (m)
          </label>
          <input
            id="defaultHeight"
            type="number"
            value={formatInputNumber(defaultSensorHeight)}
            onChange={(e) => setDefaultSensorHeight(parseFloat(e.target.value))}
            style={inputStyle}
          />
        </div>
        <div style={inputGroupStyle}>
          <label htmlFor="defaultRotation" style={labelStyle}>
            Default Rotation Rate (Hz)
          </label>
          <input
            id="defaultRotation"
            type="number"
            value={defaultSensorRotationRate}
            onChange={(e) => setDefaultSensorRotationRate(parseFloat(e.target.value))}
            style={inputStyle}
          />
        </div>

        {(selectedSensorType === 'Rotating' || selectedSensorType === 'MEMS') && (
          <>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Default Min Vertical Angle (deg)</label>
              <input
                type="number"
                step={0.1}
                value={defaultSensorParameters.minVerticalAngle}
                onChange={(e) => setDefaultSensorParameters({ minVerticalAngle: parseFloat(e.target.value) })}
                style={inputStyle}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Default Max Vertical Angle (deg)</label>
              <input
                type="number"
                step={0.1}
                value={defaultSensorParameters.maxVerticalAngle}
                onChange={(e) => setDefaultSensorParameters({ maxVerticalAngle: parseFloat(e.target.value) })}
                style={inputStyle}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Default Vertical Channels</label>
              <input
                type="number"
                step={1}
                value={defaultSensorParameters.verticalChannels}
                onChange={(e) => setDefaultSensorParameters({ verticalChannels: parseInt(e.target.value, 10) })}
                style={inputStyle}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Default Horizontal Res ({selectedSensorType === 'MEMS' ? 'firings/line' : 'firings/frame'})</label>
              <input
                type="number"
                step={100}
                value={defaultSensorParameters.horizontalResolution}
                onChange={(e) => setDefaultSensorParameters({ horizontalResolution: parseInt(e.target.value, 10) })}
                style={inputStyle}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Default Horizontal FOV (deg)</label>
              <input
                type="number"
                step={1}
                value={defaultSensorParameters.horizontalFov}
                onChange={(e) => setDefaultSensorParameters({ horizontalFov: parseFloat(e.target.value) })}
                style={inputStyle}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Default Effective Range (m)</label>
              <input
                type="number"
                step={1}
                value={defaultSensorParameters.effectiveRange}
                onChange={(e) => setDefaultSensorParameters({ effectiveRange: parseFloat(e.target.value) })}
                style={inputStyle}
              />
            </div>
          </>
        )}
        <div style={buttonContainerStyle}>
          <button onClick={onClose} style={buttonStyle}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SensorDefaultsModal;
